import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, SlidersHorizontal } from 'lucide-react';
import { Brand } from './Brand';
import { UserMenu } from './UserMenu';
import { ScopeDialog } from './ScopeDialog';
import { SearchBox } from '../search/SearchBox';
import { useSources } from '../../context/SourcesContext';
import { addRecentSearch } from '../../hooks/useRecentSearches';
import { newSearchParams, patchParams, readSearchState } from '../../lib/searchParams';
import { cx } from '../../lib/cx';

/** Sticky top bar: brand, search, scope, resources and account. */
export const AppHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sources = useSources();
  const [scopeOpen, setScopeOpen] = useState(false);

  const onSearchPage = location.pathname === '/search';
  const urlState = readSearchState(params);
  const hasQuery = onSearchPage && Boolean(urlState.q);
  const mode = sources.effectiveMode(hasQuery ? urlState.mode : sources.scope.mode);
  const match = hasQuery ? urlState.match : sources.scope.match;
  const targets = sources.searchTargets(mode);

  const disabledReason = sources.status === 'ready' && targets.labCount === 0
    ? 'No labs are selected. Open "Scope" to choose which labs to search.'
    : '';

  const runSearch = (q) => {
    addRecentSearch(q);
    const next = newSearchParams(q, { mode, match });
    const search = `?${next.toString()}`;
    // Re-running the exact same search replaces the history entry; the nonce
    // tells the search page to run it again.
    navigate(`/search${search}`, {
      replace: onSearchPage && location.search === search,
      state: { nonce: Date.now() },
    });
  };

  const applyScope = ({ scope, mode: nextMode, match: nextMatch }) => {
    sources.setScope(scope);
    if (hasQuery) {
      navigate(`/search?${patchParams(params, { mode: nextMode, match: nextMatch }).toString()}`, { replace: true });
    }
  };

  const scopeLabel = sources.status === 'ready'
    ? `${targets.labCount} lab${targets.labCount === 1 ? '' : 's'}`
    : 'Scope';
  const modeNote = mode === 'live' ? 'Live' : match === 'full' ? 'Full text' : '';

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-canvas/85 backdrop-blur-md" data-app-header>
      {/* Phones: brand and actions on one row, search below. Wider: search centred between them. */}
      <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-3 gap-y-2.5 px-4 py-2.5 sm:px-6 md:grid md:grid-cols-[1fr_minmax(0,42rem)_1fr]">
        <div className="shrink-0 whitespace-nowrap md:justify-self-start">
          <Brand />
        </div>
        <SearchBox
          key={onSearchPage ? 'search' : 'other'}
          initialValue={onSearchPage ? urlState.q : ''}
          onSearch={runSearch}
          disabledReason={disabledReason}
          className="order-last w-full md:order-none"
        />
        <div className="ml-auto flex shrink-0 items-center gap-1 whitespace-nowrap md:justify-self-end">
          <button
            type="button"
            onClick={() => setScopeOpen(true)}
            className={cx(
              'inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
              targets.labCount === 0 && sources.status === 'ready'
                ? 'border-danger/40 bg-danger/10 text-danger'
                : 'border-line-strong bg-sunken text-fg-2 hover:bg-raised hover:text-fg',
            )}
            aria-label={`Search scope: ${scopeLabel}${modeNote ? `, ${modeNote}` : ''}. Change scope`}
          >
            <SlidersHorizontal size={15} aria-hidden="true" />
            <span>{scopeLabel}</span>
            {modeNote && (
              <span className="hidden rounded bg-accent/15 px-1.5 text-xs font-medium text-accent-fg sm:inline">{modeNote}</span>
            )}
          </button>
          <Link
            to="/resources"
            className="hidden h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-raised hover:text-fg sm:inline-flex"
            aria-label="Resources and links"
            title="Resources and links"
          >
            <BookOpen size={17} aria-hidden="true" />
          </Link>
          <UserMenu />
        </div>
      </div>
      <ScopeDialog
        open={scopeOpen}
        onClose={() => setScopeOpen(false)}
        mode={mode}
        match={match}
        onApply={applyScope}
      />
    </header>
  );
};
